import {
  deleteDoc,
  getDoc,
  getDocs,
  runTransaction,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore"
import {
  getDb,
  participantDoc,
  participantsCollection,
  sessionDoc,
  toParticipant,
  toSession,
} from "@/lib/firebase/firestore"
import { isJoinable } from "@/utils/sessionLifecycle"
import { byJoinedAt } from "@/utils/timestamps"
import { stripUndefined } from "@/utils/firestoreWrite"
import type { Participant, ParticipantInput } from "@/types"

/*
 * The roster.
 *
 * WHO MAY CALL WHAT — enforced by the Firestore rules as well as by the UI.
 *
 *   Anyone holding the link: joinSession, getParticipants, updateParticipant
 *                            (their own document only), leaveSession (their own)
 *   Facilitators:            leaveSession for anyone, to remove a seat
 */

export interface JoinSessionInput {
  /**
   * The id this browser minted for the guest — see `utils/sessionIdentity`.
   *
   * Supplied by the caller rather than generated by Firestore so that rejoining
   * is idempotent: a guest who reloads, or who opens the link again a round
   * later, lands on the document they already own instead of adding a second row
   * to the roster with none of their work attached.
   */
  participantId: string
  /** The participant's own name. The only thing the join screen requires. */
  name: string
  /**
   * Their job, in their words: "Product Manager", "Designer", "CEO". Optional —
   * the room works without it, and asking twice for something nobody has to
   * answer is worse than a blank.
   */
  jobRole?: string
}

/**
 * Puts a guest in the room, or updates the name of one already there.
 *
 * There is no role argument and no election: everyone joining through the share
 * link is a participant, because the facilitator operates the app rather than
 * taking part. A participant therefore cannot arrive as facilitator — not even
 * whoever happens to open the link first. The facilitator is a signed-in account
 * named in `session.facilitatorIds`, which nothing on this path can write.
 *
 * Wrapped in a transaction so the session's state and the participant's creation
 * are atomic. Two reasons, both of which have bitten: Firestore will happily
 * create a subcollection document beneath a parent that does not exist, leaving
 * an orphan nobody ever sees; and a session that has just been completed or has
 * just expired must not accept one more person a moment later.
 *
 * `ready` and `donePhase` are only written on CREATE. A guest reopening the link
 * mid-session is correcting their name, not withdrawing the "I'm ready" they
 * already gave the facilitator.
 */
export async function joinSession(sessionId: string, input: JoinSessionInput): Promise<string> {
  const ref = participantDoc(sessionId, input.participantId)

  await runTransaction(getDb(), async (tx) => {
    const sessionSnapshot = await tx.get(sessionDoc(sessionId))
    if (!sessionSnapshot.exists()) throw new Error(`Session ${sessionId} does not exist`)

    const session = toSession(sessionSnapshot)!
    if (!isJoinable(session)) {
      throw new Error(
        session.status === "completed"
          ? "This session has finished."
          : "This session's link has expired."
      )
    }

    const existing = await tx.get(ref)
    if (existing.exists()) {
      tx.update(ref, { name: input.name, jobRole: input.jobRole ?? "" })
      return
    }

    tx.set(ref, {
      name: input.name,
      jobRole: input.jobRole ?? "",
      ready: false,
      donePhase: null,
      joinedAt: serverTimestamp(),
    })
  })

  return input.participantId
}

export async function getParticipant(
  sessionId: string,
  participantId: string
): Promise<Participant | null> {
  return toParticipant(await getDoc(participantDoc(sessionId, participantId)))
}

export async function getParticipants(sessionId: string): Promise<Participant[]> {
  const snapshot = await getDocs(participantsCollection(sessionId))
  return snapshot.docs
    .map(toParticipant)
    .filter((p): p is Participant => p !== null)
    .sort(byJoinedAt)
}

export async function updateParticipant(
  sessionId: string,
  participantId: string,
  input: ParticipantInput
): Promise<void> {
  await updateDoc(participantDoc(sessionId, participantId), stripUndefined(input))
}

/** The lobby's "I'm ready" — what the facilitator waits on before starting. */
export async function setReady(
  sessionId: string,
  participantId: string,
  ready: boolean
): Promise<void> {
  await updateParticipant(sessionId, participantId, { ready })
}

/**
 * Removes a participant. Nothing to hand over: the facilitator is not in this
 * collection, so a participant leaving can never leave the room unowned.
 *
 * Their pain points and card picks stay. The room voted on those, and a job may
 * already be built from them — deleting the author's work because they closed a
 * laptop would rewrite what the room decided.
 */
export async function leaveSession(sessionId: string, participantId: string): Promise<void> {
  await deleteDoc(participantDoc(sessionId, participantId))
}

/*
 * There is deliberately no "reset everyone's progress" call. `donePhase` names
 * the gate a participant finished, so moving the room on is what invalidates it
 * — see `utils/donePhase`. A reset would be one non-atomic write per
 * participant, racing the phase change it is meant to accompany.
 */
